import { Box, Chip, Tooltip, Typography, alpha } from "@mui/material";
import { AlertType, MatchStatus, ReimbursementSummary, ValidationError } from "@/types";
import { useAppTheme } from "@/hooks/useAppTheme";
import { ValidationErrorsCell } from "./ReviewChips";
import { formatUSD } from "@/utils/formatters/currencyFormatter";

interface ReimbursementForecastChipProps {
  /** Summary for this claimant; undefined if projection hasn't loaded. */
  summary: ReimbursementSummary | undefined;
  /** Existing validation/transformation errors — take precedence over forecast when present. */
  errors: ValidationError[];
  /** True while the projection endpoint is fetching. */
  isLoading: boolean;
}

/**
 * "What happens" cell for reimbursement documents. Renders up to two chips:
 *
 *   1. A claimant-status chip (`New claimant` or `Adds Member ID`), shown
 *      only when the claimant isn't a plain match. Mirrors the SL
 *      ForecastChip's status conventions so the two surfaces feel
 *      consistent.
 *   2. An action chip describing what approving will do to this claimant's
 *      reimbursement set: "Creates N", "All on file", warning variants
 *      for unlinked / ambiguous links, etc.
 *
 * Splitting status from action is the cleanest way to communicate "this
 * is a new claimant AND there are 2 unlinked reimbursements" without
 * cramming both signals into a single chip label that reads ambiguously
 * (e.g. "New · Creates 2 · 2 unlinked" — does "New" qualify the
 * claimant or the action?). Each chip carries its own tooltip.
 */
export const ReimbursementForecastChip = ({ summary, errors, isLoading }: ReimbursementForecastChipProps) => {
  const blockingErrors = errors.filter((e) => e.severity === AlertType.ERROR);
  if (blockingErrors.length > 0) {
    return <ValidationErrorsCell errors={blockingErrors} />;
  }
  if (isLoading) {
    return (
      <Typography variant="body2" color="text.disabled">
        Computing…
      </Typography>
    );
  }
  if (!summary) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
      <ClaimantStatusChip status={summary.match_status} />
      <ReimbursementActionChip summary={summary} />
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Claimant-status chip
// ---------------------------------------------------------------------------

const baseChipSx = {
  height: 22,
  "& .MuiChip-label": { px: 0.9, fontSize: "0.72rem", fontWeight: 600 },
};

/**
 * Renders the claimant-status chip when the match isn't a plain "matched".
 * Returns null for plain matches so the cell isn't visually noisy on the
 * common case (most reviewers see plenty of these per doc).
 */
const ClaimantStatusChip = ({ status }: { status: MatchStatus }) => {
  const { theme } = useAppTheme();

  if (status === "new") {
    return (
      <Tooltip title="New claimant — approving creates the claimant record." placement="top" arrow>
        <Chip
          label="New claimant"
          size="small"
          sx={{
            ...baseChipSx,
            bgcolor: alpha(theme.palette.success.main, 0.15),
            color: "success.dark",
          }}
        />
      </Tooltip>
    );
  }
  if (status === "matched_backfill") {
    return (
      <Tooltip
        title="Matched an existing claimant on this client. Approving will write the incoming Member ID onto the row — verify the soft-merge is correct before approving."
        placement="top"
        arrow
      >
        <Chip
          label="Adds Member ID"
          size="small"
          sx={{
            ...baseChipSx,
            bgcolor: alpha(theme.palette.info.main, 0.12),
            color: "info.dark",
          }}
        />
      </Tooltip>
    );
  }
  return null;
};

// ---------------------------------------------------------------------------
// Action chip
// ---------------------------------------------------------------------------

/**
 * The action chip describes what approving will do for this claimant's
 * reimbursement set. Variants (first match wins):
 *
 *   - "All on file"            — every row is a cross-load duplicate
 *   - "Creates N · M needs link review" — ambiguous links present
 *   - "Creates N · M unlinked"           — some rows have no SL claim
 *   - "Creates N · M on file"            — partial cross-load dup
 *   - "Creates N"                         — all linked confidently
 *
 * The noun "reimbursements" is omitted from the label — the column header
 * ("What happens") and the adjacent "Reimbursements" column with totals
 * disambiguate the count without having to spell it out per chip. Tooltips
 * carry the full claimant context.
 */
const ReimbursementActionChip = ({ summary }: { summary: ReimbursementSummary }) => {
  const { theme } = useAppTheme();

  const newCount = summary.new_reimbursement_count;
  const dupCount = summary.duplicate_on_file_count;
  const ambiguousCount = summary.ambiguous_link_count;
  const unlinkedCount = summary.unlinked_count;

  // Edge: no rows at all. Keying off rows.length (rather than newCount +
  // dupCount) because the latter would falsely render "No reimbursements"
  // if the backend ever emits a shape like {new:0, dup:0, unlinked:N} —
  // that's a backend invariant we shouldn't take a dependency on.
  if (summary.rows.length === 0) {
    return (
      <Tooltip title="No reimbursements detected for this claimant." placement="top" arrow>
        <Chip
          label="No reimbursements"
          size="small"
          variant="outlined"
          sx={{ ...baseChipSx, borderColor: "divider", color: "text.disabled" }}
        />
      </Tooltip>
    );
  }

  // All rows are cross-load duplicates — nothing will be created.
  if (newCount === 0 && dupCount > 0) {
    return (
      <Tooltip
        title={`All ${pluralize(dupCount, "reimbursement")} for ${summary.claimant_name} are already on file from a prior load. Approving creates nothing for this claimant.`}
        placement="top"
        arrow
      >
        <Chip
          label="All on file"
          size="small"
          variant="outlined"
          sx={{ ...baseChipSx, borderColor: "divider", color: "text.disabled" }}
        />
      </Tooltip>
    );
  }

  // Ambiguous links — some rows have multiple candidate SL claims. Warning
  // tint because the reviewer needs to act manually post-load.
  if (ambiguousCount > 0) {
    return (
      <Tooltip title={ambiguousTooltip(summary)} placement="top" arrow>
        <Chip
          label={`Creates ${newCount} · ${ambiguousCount} needs link review`}
          size="small"
          sx={{
            ...baseChipSx,
            bgcolor: alpha(theme.palette.warning.main, 0.15),
            color: "warning.dark",
          }}
        />
      </Tooltip>
    );
  }

  // Some rows have no SL claim found — load proceeds, but unlinked.
  if (unlinkedCount > 0) {
    return (
      <Tooltip
        title={`Approving creates ${pluralize(newCount, "reimbursement")} for ${summary.claimant_name}. ${unlinkedCount} could not be linked to a stoploss claim and will load as orphans pending manual review.`}
        placement="top"
        arrow
      >
        <Chip
          label={`Creates ${newCount} · ${unlinkedCount} unlinked`}
          size="small"
          sx={{
            ...baseChipSx,
            bgcolor: alpha(theme.palette.warning.main, 0.1),
            color: "warning.dark",
          }}
        />
      </Tooltip>
    );
  }

  // Mixed new + duplicate — informational, all confident links on the new ones.
  if (dupCount > 0) {
    return (
      <Tooltip
        title={`Approving creates ${pluralize(newCount, "reimbursement")} for ${summary.claimant_name}. ${dupCount} ${pluralize(dupCount, "row")} already on file from a prior load — those will be skipped.`}
        placement="top"
        arrow
      >
        <Chip
          label={`Creates ${newCount} · ${dupCount} on file`}
          size="small"
          sx={{
            ...baseChipSx,
            bgcolor: alpha(theme.palette.info.main, 0.12),
            color: "info.dark",
          }}
        />
      </Tooltip>
    );
  }

  // All new + all linked confidently — most common steady-state.
  return (
    <Tooltip
      title={`Approving creates ${pluralize(newCount, "reimbursement")} for ${summary.claimant_name}, linked to the resolved stoploss claim${newCount === 1 ? "" : "s"}.`}
      placement="top"
      arrow
    >
      <Chip
        label={`Creates ${newCount}`}
        size="small"
        sx={{
          ...baseChipSx,
          bgcolor: alpha(theme.palette.info.main, 0.15),
          color: "info.dark",
        }}
      />
    </Tooltip>
  );
};

const pluralize = (count: number, singular: string): string => (count === 1 ? singular : `${singular}s`);

/**
 * Builds a tooltip body listing the totals + a short description of the
 * ambiguous condition. Keeps the chip surface terse while letting reviewers
 * drill in for context.
 */
const ambiguousTooltip = (summary: ReimbursementSummary): string => {
  const total = summary.new_reimbursement_count + summary.duplicate_on_file_count;
  const reqStr = formatUSD(summary.total_requested_amount);
  const reimbStr = formatUSD(summary.total_reimbursement_amount);
  return [
    `${total} ${pluralize(total, "reimbursement")} for ${summary.claimant_name} (${reqStr} requested → ${reimbStr} reimbursed).`,
    `${summary.ambiguous_link_count} ${pluralize(summary.ambiguous_link_count, "row")} match multiple stoploss claims and need reviewer selection.`,
    `Approving will load all rows; ambiguous ones save unlinked for manual reconciliation.`,
  ].join(" ");
};
